/**
 * The Studio Handbook: how Studio works today, how to extend it with
 * integrations, and how to operate it. Pages are data the API serves to
 * readers (`backend/api/src/handbook/`), laid out with section navigation,
 * search and an on-page table of contents.
 */
import { ArrowLeft, ArrowRight, BookOpen, Clock, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, useLocation, useParams } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { HandbookBlock } from './HandbookBlocks';
import { useHandbook } from './HandbookContext';
import { HandbookHome } from './HandbookHome';
import { GROUP_ICONS, HandbookSearch } from './HandbookSearch';
import { findPage, headings, neighbours, pagePath, pagesInGroup } from './handbookContent';
import type { HandbookPageDef } from './handbookTypes';

function SectionNav() {
  const handbook = useHandbook();
  return (
    <nav aria-label="Handbook sections" className="space-y-6">
      <NavLink
        to="/"
        end
        className={({ isActive }) =>
          cn('flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')
        }
      >
        <BookOpen className="h-4 w-4" />
        Handbook home
      </NavLink>
      {handbook.groups.map((group) => {
        const Icon = GROUP_ICONS[group.id];
        return (
          <div key={group.id} className={cn(group.featured && 'rounded-xl border border-brand/25 bg-brand-muted/15 p-2')}>
            <div className="mb-1.5 flex items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Icon className={cn('h-3.5 w-3.5', group.featured && 'text-brand')} />
              {group.label}
              {group.featured ? (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand-muted px-1.5 py-0.5 text-[9px] text-brand">
                  <Sparkles className="h-2.5 w-2.5" />
                  Featured
                </span>
              ) : null}
            </div>
            <ul className="space-y-0.5">
              {pagesInGroup(handbook, group.id).map((page) => (
                <li key={page.slug}>
                  <NavLink
                    to={pagePath(page)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md border-l-2 px-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'border-brand bg-secondary/60 font-medium text-foreground'
                          : 'border-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground',
                      )
                    }
                  >
                    {page.title}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function OnThisPage({ page }: { page: HandbookPageDef }) {
  const items = useMemo(() => headings(page), [page]);
  const [current, setCurrent] = useState(items[0]?.id);

  useEffect(() => {
    if (items.length === 0) return undefined;
    // The current section is the last heading that has scrolled past the top band.
    const update = () => {
      let active = items[0]?.id;
      for (const item of items) {
        const element = document.getElementById(item.id);
        if (element && element.getBoundingClientRect().top <= 140) active = item.id;
      }
      setCurrent(active);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [items]);

  if (items.length === 0) return null;
  return (
    <nav aria-label="On this page" className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">On this page</div>
      <ul className="space-y-1 border-l border-border">
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className={cn(
                '-ml-px block border-l py-1 pl-3 text-xs transition-colors',
                current === item.id ? 'border-brand text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Article({ page }: { page: HandbookPageDef }) {
  const handbook = useHandbook();
  const group = handbook.groups.find((item) => item.id === page.group);
  const { previous, next } = neighbours(handbook, page);
  const Icon = GROUP_ICONS[page.group];
  return (
    <article className="min-w-0">
      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Handbook
        </Link>
        <span>/</span>
        <span className="inline-flex items-center gap-1.5">
          <Icon className="h-3 w-3" />
          {group?.label}
        </span>
      </div>
      <h1 className="text-3xl font-bold tracking-tight text-foreground">{page.title}</h1>
      <p className="mt-3 text-lg leading-8 text-muted-foreground">{page.summary}</p>
      <div className="mt-4 flex items-center gap-1.5 border-b border-border pb-6 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {page.minutes} min read
      </div>
      <div className="pb-4">
        {page.blocks.map((block, index) => (
          <HandbookBlock key={index} block={block} />
        ))}
      </div>
      <div className="mt-12 grid gap-3 border-t border-border pt-6 sm:grid-cols-2">
        {previous ? (
          <Link to={pagePath(previous)} className="group rounded-xl border border-border p-4 transition-colors hover:border-brand/40">
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <ArrowLeft className="h-3 w-3" />
              Previous
            </div>
            <div className="mt-1 text-sm font-medium text-foreground">{previous.title}</div>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={pagePath(next)} className="group rounded-xl border border-border p-4 text-right transition-colors hover:border-brand/40">
            <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
              Next
              <ArrowRight className="h-3 w-3" />
            </div>
            <div className="mt-1 text-sm font-medium text-foreground">{next.title}</div>
          </Link>
        ) : null}
      </div>
    </article>
  );
}

export function HandbookPage() {
  const handbook = useHandbook();
  const { group, slug } = useParams();
  const location = useLocation();
  const page = group || slug ? findPage(handbook, group, slug) : undefined;

  useEffect(() => {
    if (location.hash) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);

  if ((group || slug) && !page) return <Navigate to="/" replace />;

  return (
    // Docs layout under the Handbook header: section nav on the left, the page, and its table of contents.
    <div className="flex min-h-full w-full">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card/20 lg:block">
        <div className="sticky top-14 max-h-[calc(100vh-3.5rem)] space-y-6 overflow-y-auto px-4 py-6">
          <SectionNav />
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-6 py-8 lg:px-10">
        <div className="mb-6 md:hidden">
          <HandbookSearch />
        </div>
        {page ? (
          <div className="flex justify-between gap-12">
            <div className="min-w-0 max-w-[52rem] flex-1">
              <Article page={page} />
            </div>
            <aside className="hidden w-56 shrink-0 xl:block">
              <div className="sticky top-22">
                <OnThisPage page={page} />
              </div>
            </aside>
          </div>
        ) : (
          <HandbookHome />
        )}
      </main>
    </div>
  );
}
